import { formatDate } from '@angular/common';
import { PlannerBucket, PlannerPlan, PlannerTask, PlannerTaskDetails } from '@microsoft/microsoft-graph-types';
import { OutlookTask } from '@microsoft/microsoft-graph-types-beta';
import { parseISO } from 'date-fns';
import { Observable } from 'rxjs';

export enum TaskType {
  Outlook = 0,
  Planner = 1,
}

export enum TaskStatus {
  NOTSTARTED = 0,
  INPROGRESS = 1,
  COMPLETED = 2,
  WAITINGONOTHERS = 3,
  DEFERRED = 4
}

export class MyTask {
  public Type: TaskType;
  public AssignedTo: string;
  public Body: string;
  public CompletedDate: Date;
  public CompletedDateFormatted: string;
  public DueDate: Date;
  public DueDateFormatted: string;
  public StartDate: Date;
  public StartDateFormatted: string;
  public Status: TaskStatus;
  public Created: Date;
  public CreatedDateFormatted: string;
  public Importance: string;
  public Owner: string;
  public ParentFolderId: string;
  public Subject: string;
  public Url: string;
  public Id: string;
  public Etag: string;
  public PlannerTaskDetails$: Observable<PlannerTaskDetails>;
  public PlannerPlan$: Observable<PlannerPlan>;
  public PlannerBucket$: Observable<PlannerBucket>;

  constructor(opt?: Partial<MyTask>) {
    Object.assign(this, opt);
  }

  static fromOutlook(item: OutlookTask, localeId: string, defaultTaskFolderId: string) {

    const timeZoneSuffix = 'Z';
    const completedDate = item.completedDateTime ? parseISO(item.completedDateTime.dateTime + timeZoneSuffix) : null;
    const dueDate = item.dueDateTime ? parseISO(item.dueDateTime.dateTime + timeZoneSuffix) : null;
    const startDate = item.startDateTime ? parseISO(item.startDateTime.dateTime + timeZoneSuffix) : null;

    // Id should be available, but we previously had reports of "short ids" being used (see DevOps #22228). I have not seen it now, but link to inbox as a fallback just to be sure it does not crash.
    let itemUrl = 'https://to-do.office.com/lists/inbox';
    if (item.id && item.id.length > 30) {
      itemUrl = `https://to-do.office.com/tasks/id/${item.id}/details`;
    }

    const fancypants = new MyTask({
      Type: TaskType.Outlook,
      Body: item.body.content,
      Status: TaskStatus[item.status.toLocaleUpperCase()],
      Subject: item.subject,
      Owner: item.owner,
      CompletedDate: completedDate,
      CompletedDateFormatted: completedDate ? formatDate(completedDate, 'mediumDate', localeId) : '',
      DueDate: dueDate,
      DueDateFormatted: dueDate ? formatDate(dueDate, 'mediumDate', localeId) : '',
      StartDate: startDate,
      StartDateFormatted: startDate ? formatDate(startDate, 'shortTime', localeId) : '',
      Created: item.createdDateTime ? parseISO(item.createdDateTime + timeZoneSuffix) : null,
      CreatedDateFormatted: item.createdDateTime ? formatDate(item.createdDateTime, 'mediumDate', localeId) : '',
      Url: itemUrl,
      Id: item.id
    });
    return fancypants;
  }

  static fromPlanner(item: PlannerTask, localeId: string, tenantName: string) {

    let status = TaskStatus.NOTSTARTED;
    if (item.percentComplete === 50) {
      status = TaskStatus.INPROGRESS;
    } else if (item.percentComplete === 100) {
      status = TaskStatus.COMPLETED;
    }

    return new MyTask({
      Type: TaskType.Planner,
      Body: '',
      Subject: item.title,
      Status: status,
      Owner: '',
      CompletedDate: item.completedDateTime ? parseISO(item.completedDateTime) : null,
      CompletedDateFormatted: item.completedDateTime ? formatDate(item.completedDateTime, 'mediumDate', localeId) : '',
      DueDate: item.dueDateTime ? parseISO(item.dueDateTime) : null,
      DueDateFormatted: item.dueDateTime ? formatDate(item.dueDateTime, 'mediumDate', localeId) : '',
      StartDate: item.startDateTime ? parseISO(item.startDateTime) : null,
      StartDateFormatted: item.startDateTime ? formatDate(item.startDateTime, 'shortTime', localeId) : '',
      Created: item.createdDateTime ? parseISO(item.createdDateTime) : null,
      CreatedDateFormatted: item.createdDateTime ? formatDate(item.createdDateTime, 'mediumDate', localeId) : '',
      Url: `https://tasks.office.com/${tenantName}/en-US/Home/Task/${item.id}?Type=TaskLink&Channel=Link`,
      Id: item.id,
      Etag: item['@odata.etag'],
    });
  }
}
